import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';

export interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string[];
  author?: string;
  canonicalUrl?: string;
  ogType?: 'website' | 'article' | 'profile';
  ogImage?: string;
  ogImageAlt?: string;
  twitterCard?: 'summary' | 'summary_large_image' | 'app' | 'player';
  publishedTime?: string;
  modifiedTime?: string;
  articleAuthor?: string;
  articleSection?: string;
  articleTags?: string[];
  noIndex?: boolean;
  noFollow?: boolean;
  structuredData?: object | object[];
}

interface SEOConfig {
  siteName: string;
  siteUrl: string;
  defaultTitle: string;
  defaultDescription: string;
  defaultKeywords: string[];
  defaultImage: string;
  twitterHandle: string;
  facebookAppId?: string;
  locale: string;
}

const seoConfig: SEOConfig = {
  siteName: 'Bearly Coding Lab',
  siteUrl: 'https://bearlycodinglab.com',
  defaultTitle: 'Bearly Coding Lab - Every Problem Has A Solution',
  defaultDescription: 'Expert software development solutions, innovative projects, and comprehensive JavaScript tutorials. We find the right path forward for complex technical challenges.',
  defaultKeywords: [
    'software development',
    'web development',
    'javascript tutorials',
    'react developer',
    'fullstack developer',
    'coding tutorials',
    'programming blog',
    'technical solutions'
  ],
  defaultImage: '/images/og-default.jpg',
  twitterHandle: '@BearlyCodeLab',
  locale: 'en_US'
};

const SEO: React.FC<SEOProps> = ({
  title,
  description,
  keywords = [],
  author = 'Bearly Coding Lab Team',
  canonicalUrl,
  ogType = 'website',
  ogImage,
  ogImageAlt,
  twitterCard = 'summary_large_image',
  publishedTime,
  modifiedTime,
  articleAuthor,
  articleSection,
  articleTags = [],
  noIndex = false,
  noFollow = false,
  structuredData
}) => {
  const { i18n } = useTranslation();
  const currentLang = i18n.language || 'en';

  // Build full title
  const fullTitle = title
    ? `${title} | ${seoConfig.siteName}`
    : seoConfig.defaultTitle;

  // Use provided description or default
  const metaDescription = description || seoConfig.defaultDescription;

  // Combine keywords with defaults
  const allKeywords = [...new Set([...keywords, ...seoConfig.defaultKeywords])];

  // Build canonical URL
  const canonical = canonicalUrl
    ? `${seoConfig.siteUrl}${canonicalUrl}`
    : seoConfig.siteUrl;

  // Build OG image URL
  const ogImageUrl = ogImage
    ? ogImage.startsWith('http')
      ? ogImage
      : `${seoConfig.siteUrl}${ogImage}`
    : `${seoConfig.siteUrl}${seoConfig.defaultImage}`;

  // Build robots directive
  const robotsContent = [
    noIndex ? 'noindex' : 'index',
    noFollow ? 'nofollow' : 'follow'
  ].join(', ');

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <html lang={currentLang} />
      <title>{fullTitle}</title>
      <meta name="description" content={metaDescription} />
      {allKeywords.length > 0 && (
        <meta name="keywords" content={allKeywords.join(', ')} />
      )}
      <meta name="author" content={author} />
      <meta name="robots" content={robotsContent} />
      <link rel="canonical" href={canonical} />

      {/* Bilingual Support - hreflang Tags */}
      <link rel="alternate" hrefLang="en" href={canonical} />
      <link rel="alternate" hrefLang="ko" href={canonical} />
      <link rel="alternate" hrefLang="x-default" href={canonical} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} />
      <meta property="og:site_name" content={seoConfig.siteName} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={ogImageUrl} />
      {ogImageAlt && <meta property="og:image:alt" content={ogImageAlt} />}
      <meta property="og:locale" content={seoConfig.locale} />

      {/* Article specific Open Graph tags */}
      {ogType === 'article' && (
        <>
          {publishedTime && (
            <meta property="article:published_time" content={publishedTime} />
          )}
          {modifiedTime && (
            <meta property="article:modified_time" content={modifiedTime} />
          )}
          {articleAuthor && (
            <meta property="article:author" content={articleAuthor} />
          )}
          {articleSection && (
            <meta property="article:section" content={articleSection} />
          )}
          {articleTags.map((tag, index) => (
            <meta key={index} property="article:tag" content={tag} />
          ))}
        </>
      )}

      {/* Twitter Card */}
      <meta name="twitter:card" content={twitterCard} />
      <meta name="twitter:site" content={seoConfig.twitterHandle} />
      <meta name="twitter:creator" content={seoConfig.twitterHandle} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={metaDescription} />
      <meta name="twitter:image" content={ogImageUrl} />
      {ogImageAlt && <meta name="twitter:image:alt" content={ogImageAlt} />}

      {/* Additional SEO Meta Tags */}
      <meta name="format-detection" content="telephone=no" />
      <meta name="theme-color" content="#22C55E" />
      <meta name="mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />

      {/* Structured Data (JSON-LD) */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(
            Array.isArray(structuredData)
              ? structuredData
              : structuredData
          )}
        </script>
      )}
    </Helmet>
  );
};

export default SEO;

// Utility functions for creating structured data

export const createOrganizationSchema = () => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": seoConfig.siteName,
  "legalName": "Bearly Coding Lab",
  "alternateName": "곰곰이 코딩랩",
  "url": seoConfig.siteUrl,
  "logo": `${seoConfig.siteUrl}/logo.png`,
  "email": "contact@bearlycodinglab.com",
  "description": seoConfig.defaultDescription,
  "sameAs": [
    "https://www.instagram.com/bearlycodinglab.kor/",
    "https://www.youtube.com/@곰곰이코딩랩"
  ],
  "contactPoint": {
    "@type": "ContactPoint",
    "contactType": "Customer Support",
    "email": "contact@bearlycodinglab.com"
  }
});

export const createWebPageSchema = (props: {
  title: string;
  description: string;
  url: string;
  breadcrumbs?: Array<{ name: string; url: string }>;
}) => ({
  "@context": "https://schema.org",
  "@type": "WebPage",
  "name": props.title,
  "description": props.description,
  "url": props.url,
  "inLanguage": "en-US",
  "isPartOf": {
    "@type": "WebSite",
    "name": seoConfig.siteName,
    "url": seoConfig.siteUrl
  }
});

export const createArticleSchema = (props: {
  title: string;
  description: string;
  url: string;
  publishedTime: string;
  modifiedTime?: string;
  author: string;
  section: string;
  keywords: string[];
  image?: string;
  readTime?: string;
}) => ({
  "@context": "https://schema.org",
  "@type": "Article",
  "headline": props.title,
  "description": props.description,
  "url": props.url,
  "datePublished": props.publishedTime,
  "dateModified": props.modifiedTime || props.publishedTime,
  "author": {
    "@type": "Person",
    "name": props.author
  },
  "publisher": {
    "@type": "Organization",
    "name": seoConfig.siteName,
    "logo": {
      "@type": "ImageObject",
      "url": `${seoConfig.siteUrl}/logo.png`
    }
  },
  "articleSection": props.section,
  "keywords": props.keywords.join(', '),
  ...(props.image && {
    "image": {
      "@type": "ImageObject",
      "url": props.image
    }
  }),
  "inLanguage": "en-US",
  "mainEntityOfPage": {
    "@type": "WebPage",
    "@id": props.url
  }
});

export const createBreadcrumbSchema = (breadcrumbs: Array<{ name: string; url: string }>) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": breadcrumbs.map((crumb, index) => ({
    "@type": "ListItem",
    "position": index + 1,
    "name": crumb.name,
    "item": `${seoConfig.siteUrl}${crumb.url}`
  }))
});

export const createWebSiteSchema = () => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  "name": seoConfig.siteName,
  "url": seoConfig.siteUrl,
  "description": seoConfig.defaultDescription,
  "potentialAction": {
    "@type": "SearchAction",
    "target": {
      "@type": "EntryPoint",
      "urlTemplate": `${seoConfig.siteUrl}/search?q={search_term_string}`
    },
    "query-input": "required name=search_term_string"
  }
});
